import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  HtmlTrans,
  Button,
  MUIDataTable,
  MUIDataTableColumn,
  Pill,
  Typography,
  useDataTable, Link,
} from 'web-library';
import { StatusCodes } from 'http-status-codes';
import {
  ApplicationStatus,
  CandidateRegisteredExamListingResponse,
  ExamRegistrationType,
} from '../../../api/candidate/exam/model';
import { candidateService } from '../../../api';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import DeleteDraftDialog, { IDialogStateHandler } from './listing/DeleteDraftDialog';
import ExamListingActionButtons from './listing/ExamListingActionButtons';
import RouteKey from '../../../routes/key';
import { useGetClosedExamTypes } from '../logic/useClosedExamTypes';
import { GET_EXAM_REGISTRATION } from '../../../api/candidate/exam/@candidate/queries-services/keys';
import ProceedSupplementaryModal from './ProceedSupplementaryModal';
import { parseLocationSearch } from '../../../utils/StringUtils';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const { accessArrangement, exam, error } = RouteKey.child;
const { root } = RouteKey['access-arrangement'].child;

const { examRegistrations, selectSubject } = RouteKey.exam.child;
const { generic } = RouteKey.error.child;
const { FORBIDDEN } = StatusCodes;

const NonNationalExamType = ['AEIS', 'S_AEIS', 'SPERS_SEC', 'SPE'];

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#exam-registration-listing': {
      '& .MuiTableRow-root': {
        verticalAlign: 'top',
        '&:not(:last-child)': {
          borderBottom: `1px solid ${theme.color.border.borderFocused} !important`,
        },
        '& .MuiTableCell-root': {
          '&[data-colindex="0"]': {
            minWidth: 200,
          },
        },
      },
      '& .MuiTableBody-root': {
        '& .MuiButtonBase-root': {
          height: '20px',
          padding: '12px',
        },
      },
    },
  },
  table: {
    marginBottom: theme.spacing(3),
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  amendOrViewButton: {
    padding: '0px !important',
    marginRight: theme.spacing(4),
    width: 'max-content',
  },
  tabsHeader: {
    '& .MuiTab-root': {
      textTransform: 'none',
    },
    marginBottom: theme.spacing(4),
    '& button': {
      maxWidth: '326px',
    },
  },
  examLevelCol: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
  instruction: {
    marginBottom: theme.spacing(3),
  },
  iconContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  deleteButton: {
    color: theme.color.icon.iconTrash,
    whiteSpace: 'nowrap',
    padding: `${theme.spacing(1)} !important`,
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    alignItems: 'center',
  },
  link: {
     ...typeTheme(theme, 'buttonSmall'),
  },
  accessArrangement: {
    marginTop: theme.spacing(3),
  },
  viewTransactionLink: {
    fontWeight: 800,
  },
  lastUpdatedText: {
    color: theme.color.text.textTertiary,
    marginTop: theme.spacing(2),
  },
}));

const dataColumns = (
  data: CandidateRegisteredExamListingResponse[],
  classes: Record<string,string>,
  history: any,
  theme: AugmentedTheme,
  dialogStateHandler: IDialogStateHandler,
  t: TFunction<'translation'>
): MUIDataTableColumn[] => [
    {
      name: 'applicationID',
      label: 'Application ID',
      options: {
        sort: true,
        setCellProps: () => ({ style: { width: '13%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography id={`app-${dataIndex}`} key={data[dataIndex].applicationID}>
            {data[dataIndex].applicationID || ''}
          </Typography>
        ),
      },
    },
    {
      name: 'examLevels',
      label: 'Exam Level(s)',
      options: {
        sort: true,
        setCellProps: () => ({ style: { width: '34%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {data[dataIndex].examLevels.map((d) => (
              <Typography key={dataIndex} className={classes.examLevelCol}>
                {d.examLevel}
              </Typography>
            ))}
          </>
        ),
      },
    },
    {
      name: 'applicationDateTime',
      label: 'Application Date/Time',
      options: {
        sort: true,
        setCellProps: () => ({ style: { width: '13%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography>
            {data[dataIndex].applicationDateTime
              ? stringToLongDateAndShortTime(data[dataIndex].applicationDateTime)
              : '-'}
          </Typography>
        ),
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        sort: true,
        setCellProps: () => ({ style: { width: '10%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          if (data[dataIndex].applicationStatus) {
            const { applicationStatus } = data[dataIndex];
            return (
              <>
                <Pill
                  variant={applicationStatus === ApplicationStatus.REGISTERED ? 'positive' : 'info'}
                  label={data[dataIndex].applicationStatus}
                />
                <Typography className={classes.lastUpdatedText} variant="subtitle2">
                  {t('exam->>ViewRegisteredExamsListing.LastUpdatedDateTime', {
                    lastUpdatedDateTime:
                      stringToLongDateAndShortTime(data[dataIndex].lastUpdatedDateTime) || '-',
                  })}
                </Typography>
              </>
            );
          }
          return <Pill variant="info" label="" />;
        },
      },
    },
    {
      name: 'entryProof',
      label: 'Entry Proof',
      options: {
        setCellProps: () => ({ style: { width: '17%', paddingTop: '18px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography>
            {data[dataIndex].entryProofLastUpdate === ''
              ? '-'
              : `${t('exam->>ViewRegisteredExamsListing.EntryProofLastUpdated', {
                entryProofLastUpdated: stringToSemanticDisplay(
                  data[dataIndex].entryProofLastUpdate
                ),
              })}`}
          </Typography>
        ),
      },
    },
    {
      name: 'viewDetail',
      label: t('exam->>ViewRegisteredExamsListing.Actions'),
      options: {
        setCellProps: () => ({ style: { width: '13%' } }),
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          ExamListingActionButtons({
            data,
            styling: {
              classes,
              theme,
            },
            hooks: {
              history,
              t,
            },
            MUIDataIndex: {
              dataIndex,
              rowIndex,
            },
            dialogStateHandler,
          }),
      },
    },
  ];

const isRegisteredExam = (item: CandidateRegisteredExamListingResponse) =>
  [ApplicationStatus.REGISTERED, ApplicationStatus.NON_NATIONAL_PENDING].includes(
    item.applicationStatus
  );

const isHavingRegisteredExam = (data: any) => {
  if (Array.isArray(data)) {
    return !data.length || data.some(isRegisteredExam);
  }
  return false;
};

const ExamRegistrationsListingTablesDialog = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory<any>();
  const theme = useTheme();
  const { state, dispatch } = useDataTable();
  const isFetching = false;
  const [hasExam, setHasExam] = useState(false);
  const [isNonNationalExam, setIsNonNationalExam] = useState(false);
  const [proceedSupplementaryModal, setProceedSupplementaryModal] = useState<boolean>(false);
  const [deleteUuid, setDeleteUuid] = useState<string>('');
  const [deleteExamType, setDeleteExamType] = useState<string>('');
  const location = useLocation<{ uuid?: string }>();
  const queryParams = parseLocationSearch(location.search);

  const { data } = candidateService.exam.candidate.useGetExamRegistrationsListing(
    queryParams.uuid,
    (res) => {
      if (res.length) {
        setHasExam(
          res.some(
            (registeredExam) =>
              ExamRegistrationType.WITHDRAWAL !== registeredExam.examRegistrationType &&
              ApplicationStatus.DRAFT !== registeredExam.applicationStatus
          )
        );

        setIsNonNationalExam(
          res.some((registeredExam) => NonNationalExamType.includes(registeredExam.examType))
        );
      }
    },
    (errors) => {
      if (errors.response?.status === FORBIDDEN) {
        history.push(`/${error}/${generic}`);
      }
    }
  );

  const { isWindowOpenedCallback: isWindowOpened } = useGetClosedExamTypes(data);

  const [isDeleteDraftDialogOpen, setDeleteDraftDialogOpen] = useState(false);

  const onClickHere = () => history.push(`/${accessArrangement}/${root.route}`);

  const isDisplayAmendmentErrorMessage = data?.some(
    (d: CandidateRegisteredExamListingResponse): boolean =>
      d.examRegistrationType === 'AMENDMENT' && !d.timeWindowOpen
  );

  const isDisplayWithdrawalErrorMessage = data?.some(
    (d: CandidateRegisteredExamListingResponse): boolean =>
      d.examRegistrationType === 'WITHDRAWAL' &&
      !NonNationalExamType.includes(d.examType) &&
      !d.timeWindowOpen
  );

  return (
    <>
      {data && !isWindowOpened().opened && !isHavingRegisteredExam(data) && !isNonNationalExam && (
        <Alert
          className={classes.alert}
          severity="error"
          message={t('exam->>ViewRegisteredExamsListing.RegistrationUnavailable', {
            closedExamType: isWindowOpened()?.closedExamType.join(', '),
          })}
          variant="filled"
        />
      )}
      {isDisplayAmendmentErrorMessage && (
        <Alert
          className={classes.alert}
          variant="filled"
          severity="error"
          message={t('exam->>ViewRegisteredExamsListing.UnavailableSubjectAmendment')}
        />
      )}
      {isDisplayWithdrawalErrorMessage && (
        <Alert
          className={classes.alert}
          variant="filled"
          severity="error"
          message={t('exam->>ViewRegisteredExamsListing.UnavailableWithdrawal')}
        />
      )}
      {data && (
        <Box id="exam-registration-listing">
          <MUIDataTable
            state={state}
            dispatch={dispatch}
            isLoading={isFetching}
            columns={dataColumns(
              data,
              classes,
              history,
              theme,
              {
                isDeleteDraftDialogOpen,
                setDeleteDraftDialogOpen,
                setProceedSupplementaryModal,
                setDeleteUuid,
                setDeleteExamType,
              },
              t
            )}
            data={data}
            emptyDataLabel={t('exam->>ViewRegisteredExamsListing.HaveNotRegistered')}
            emptyDataLabelAdditional={
              <HtmlTrans i18nKey="exam->>ViewRegisteredExamsListing.ExamRegistrationInstruction" />
            }
            options={{
              sort: {
                sortable: true
              },
            }}
          />
        </Box>
      )}

      {hasExam && !isNonNationalExam && (
        <Typography className={classes.accessArrangement} variant="body1">
          {t('exam->>ViewRegisteredExamsListing.AccessArrangement')}
          <Link id="link-applyAccessArrangement" className={classes.link} onClick={onClickHere}>
            {t('exam->>ViewRegisteredExamsListing.ClickHere')}
          </Link>
          .
        </Typography>
      )}

      <DeleteDraftDialog
        draftId={deleteUuid}
        dialogStateHandler={{
          isDeleteDraftDialogOpen,
          setDeleteDraftDialogOpen,
          setProceedSupplementaryModal,
          setDeleteUuid,
          setDeleteExamType,
        }}
        queryKeyToInvalidate={GET_EXAM_REGISTRATION}
      />
      <ProceedSupplementaryModal
        modelOpen={proceedSupplementaryModal}
        setModelOpen={setProceedSupplementaryModal}
        callbackMethod={(uuid) => {
          history.push(`/${exam}/${examRegistrations.route}/${uuid}/${selectSubject.route}`);
        }}
        draftUuid={deleteUuid}
        examType={deleteExamType}
      />
    </>
  );
};

export default ExamRegistrationsListingTablesDialog;
